import React from 'react';

// Horizontal side-scroll state for a scrolling row: which chevrons to show, and paging the row by
// roughly one item. Shared by the section product scroller (ProductScroller) and the product-page
// image gallery so both get the same arrow behavior from one implementation.
//
//   const { ref, canScrollLeft, canScrollRight, scrollByItem } = useSideScroll('.product-thumb', 8, [images]);
//   <div ref={ref} onScroll={onScroll}>…</div>
//
// itemSelector picks the element whose width defines a page step; gap is the row's flex gap.
// deps re-measures when the row's contents change (arrow visibility depends on scrollWidth).
export function useSideScroll(itemSelector: string, gap: number, deps: React.DependencyList = []) {
    const ref = React.useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);

    const checkScroll = React.useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        // +1 guards against sub-pixel rounding leaving the right arrow perpetually visible.
        setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
    }, []);

    // Recompute arrow visibility on mount, when the row's contents change, and on any resize of it.
    React.useEffect(() => {
        checkScroll();
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(() => checkScroll());
        ro.observe(el);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkScroll, ...deps]);

    const scrollByItem = React.useCallback((dir: 'left' | 'right') => {
        const el = ref.current;
        if (!el) return;
        // Page by ~one visible item (first item's width + the row gap), falling back to a sensible step.
        const first = el.querySelector(itemSelector) as HTMLElement | null;
        const step = first ? first.offsetWidth + gap : el.clientWidth;
        el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
    }, [itemSelector, gap]);

    return { ref, canScrollLeft, canScrollRight, checkScroll, scrollByItem };
}
